import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, ExternalLink, X, CheckCircle, XCircle } from 'lucide-react';
import ConversationView from '../components/ConversationView';

const SCENARIO_LABELS = {
  1: 'Replied then ghosted',
  2: 'Submitted, no response',
  3: "Saw post, didn't submit"
};

const STATUS_BADGE = {
  pending: 'badge-amber',
  sent: 'badge-blue',
  converted: 'badge-green',
  closed: 'badge-gray'
};

const CLASSIFICATION_LABELS = { hot: 'Hot', warm: 'Warm', cold: 'Cold' };
const CLASSIFICATION_BADGE = { hot: 'badge-red', warm: 'badge-amber', cold: 'badge-blue' };

const INDUSTRY_OPTIONS = [
  'Healthcare',
  'FinTech',
  'Real Estate/PropTech',
  'EdTech',
  'Consulting/Enterprise',
  'SaaS/General',
  'B2C'
];

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
};

// Pre-fill the conversion form's notes field with the Upwork context block
// in the format the team expects. Amit will append the LinkedIn first
// message at the bottom (using "BDE:" prefix) before saving.
const buildPrefilledNotes = (referral) => {
  const conversationLine = referral.conversation
    ? referral.conversation
    : '(no conversation — saw post / no response)';

  return [
    `Upwork Job: ${referral.upwork_job_title}`,
    `Upwork Job URL: ${referral.upwork_job_url}`,
    '',
    'Job Description:',
    referral.upwork_job_description,
    '',
    'Upwork Conversation:',
    conversationLine
  ].join('\n');
};

const UpworkReferralDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isReadOnly } = useAuth();

  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Convert modal state
  const [showConvert, setShowConvert] = useState(false);
  const [convertForm, setConvertForm] = useState({
    prospect_name: '',
    linkedin_url: '',
    company: '',
    industry: '',
    classification: '',
    notes: '',
    lead_date: new Date().toISOString().split('T')[0],
    bde_id: '',
    // Connection-note tracking — mandatory, same rules as the main Lead form.
    is_note_sent: '',
    connection_note: ''
  });
  const [industryIsOther, setIndustryIsOther] = useState(false);
  const [convertError, setConvertError] = useState('');
  const [submittingConvert, setSubmittingConvert] = useState(false);
  const [assigneeOptions, setAssigneeOptions] = useState([]);

  // Close modal state
  const [showClose, setShowClose] = useState(false);
  const [closeReason, setCloseReason] = useState('');
  const [submittingClose, setSubmittingClose] = useState(false);

  const fetchReferral = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/upwork-referrals/${id}`);
      setReferral(res.data.referral);
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to load referral.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignees = async () => {
    try {
      const res = await api.get('/users');
      const active = (res.data.users || [])
        .filter(u => u.is_active && ['admin', 'team_lead', 'bde'].includes(u.role));
      setAssigneeOptions(active);
    } catch (_) {
      // Non-fatal — modal will fall back to a single "you" option.
      setAssigneeOptions([]);
    }
  };

  useEffect(() => { fetchReferral(); }, [id]);

  const openConvert = () => {
    setConvertForm({
      prospect_name: '',
      linkedin_url: referral.linkedin_url,
      company: '',
      industry: '',
      classification: '',
      notes: buildPrefilledNotes(referral),
      lead_date: new Date().toISOString().split('T')[0],
      bde_id: user.id,
      is_note_sent: '',
      connection_note: ''
    });
    setIndustryIsOther(false);
    setConvertError('');
    setShowConvert(true);
    fetchAssignees();
  };

  const closeConvert = () => {
    setShowConvert(false);
    setConvertError('');
  };

  const validateConvert = () => {
    if (!convertForm.prospect_name.trim()) return 'Prospect name is required.';
    if (!convertForm.linkedin_url.trim()) return 'LinkedIn URL is required.';
    if (!convertForm.linkedin_url.toLowerCase().includes('linkedin.com')) {
      return 'LinkedIn URL must be a valid linkedin.com link.';
    }
    if (!convertForm.company.trim()) return 'Company is required.';
    if (!convertForm.classification) return 'Classification is required (Hot/Warm/Cold).';
    if (!convertForm.bde_id) return 'Assigned BDE is required.';
    if (convertForm.is_note_sent !== true && convertForm.is_note_sent !== false) {
      return 'Please select whether a connection note was sent.';
    }
    if (convertForm.is_note_sent === true && !convertForm.connection_note.trim()) {
      return 'Please paste the note message.';
    }
    return null;
  };

  const handleConvertSubmit = async (e) => {
    e.preventDefault();
    const v = validateConvert();
    if (v) { setConvertError(v); return; }
    setConvertError('');
    setSubmittingConvert(true);
    try {
      const res = await api.post(`/upwork-referrals/${id}/convert`, convertForm);
      // Tell Layout to refetch the sidebar badge count.
      window.dispatchEvent(new Event('upwork-referrals-changed'));
      toast.success('Referral converted to lead.');
      navigate(`/leads/${res.data.lead_id}`);
    } catch (err) {
      setConvertError(err.response?.data?.error || 'Failed to convert referral.');
    } finally {
      setSubmittingConvert(false);
    }
  };

  const handleCloseSubmit = async () => {
    if (!closeReason.trim()) {
      toast.error('Reason is required.');
      return;
    }
    setSubmittingClose(true);
    try {
      await api.post(`/upwork-referrals/${id}/close`, {
        close_reason: closeReason.trim()
      });
      // Tell Layout to refetch the sidebar badge count.
      window.dispatchEvent(new Event('upwork-referrals-changed'));
      toast.success('Referral closed.');
      setShowClose(false);
      setCloseReason('');
      await fetchReferral();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to close referral.');
    } finally {
      setSubmittingClose(false);
    }
  };

  if (loading) return <div className="empty-state"><p>Loading referral...</p></div>;
  if (err) return (
    <div className="empty-state">
      <p style={{ color: 'var(--red)' }}>{err}</p>
      <button
        onClick={() => navigate('/upwork-referrals')}
        className="btn btn-secondary"
        style={{ marginTop: 12 }}
      >
        Back to Referrals
      </button>
    </div>
  );
  if (!referral) return null;

  // Convert / Close are valid from pending OR sent (connection request out, no reply yet).
  const canTakeAction = !isReadOnly && (referral.status === 'pending' || referral.status === 'sent');

  return (
    <div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        flexWrap: 'wrap', gap: 12, marginBottom: 16
      }}>
        <div>
          <button
            onClick={() => navigate('/upwork-referrals')}
            className="btn btn-secondary btn-sm"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 8 }}
          >
            <ArrowLeft size={14} /> Back to Referrals
          </button>
          <h2 style={{ marginBottom: 4 }}>{referral.upwork_job_title}</h2>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            color: '#6B7280', fontSize: 14, flexWrap: 'wrap'
          }}>
            <span className={`badge ${STATUS_BADGE[referral.status]}`}>{referral.status}</span>
            <span>·</span>
            <span>Scenario {referral.scenario}: {SCENARIO_LABELS[referral.scenario]}</span>
            <span>·</span>
            <span>Submitted by {referral.original_upwork_bde_name}</span>
            <span>·</span>
            <span>{formatDate(referral.created_at)}</span>
          </div>
        </div>
        {canTakeAction && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={openConvert}
              className="btn btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <CheckCircle size={14} /> Convert to Lead
            </button>
            <button
              onClick={() => setShowClose(true)}
              className="btn btn-secondary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <XCircle size={14} /> Mark Closed
            </button>
          </div>
        )}
      </div>

      {referral.status === 'converted' && referral.converted_lead_id && (
        <div className="card" style={{ marginBottom: 16, background: '#ECFDF5', borderLeft: '4px solid #10B981' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#065F46' }}>
            <CheckCircle size={16} />
            <span>
              Converted to{' '}
              <Link
                to={`/leads/${referral.converted_lead_id}`}
                style={{ color: '#065F46', fontWeight: 600 }}
              >
                lead #{referral.converted_lead_id}
              </Link>{' '}
              on {formatDate(referral.closed_at)}.
            </span>
          </div>
        </div>
      )}

      {referral.status === 'closed' && (
        <div className="card" style={{ marginBottom: 16, background: '#FEF3C7', borderLeft: '4px solid #F59E0B' }}>
          <div style={{ fontSize: 14, color: '#92400E' }}>
            <strong>Closed</strong> on {formatDate(referral.closed_at)}.
            {referral.close_reason && (
              <div style={{ marginTop: 4 }}>
                <em>Reason:</em> {referral.close_reason}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 12 }}>Prospect</h3>
        <div style={{ display: 'flex', gap: 12, padding: '6px 0' }}>
          <div style={{ minWidth: 140, fontSize: 13, color: '#6B7280', fontWeight: 500 }}>LinkedIn</div>
          <div style={{ fontSize: 14 }}>
            <a
              href={referral.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--blue)', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              {referral.linkedin_url} <ExternalLink size={12} />
            </a>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 12 }}>Upwork Job</h3>
        <div style={{ display: 'flex', gap: 12, padding: '6px 0' }}>
          <div style={{ minWidth: 140, fontSize: 13, color: '#6B7280', fontWeight: 500 }}>Title</div>
          <div style={{ fontSize: 14, color: '#111827' }}>{referral.upwork_job_title}</div>
        </div>
        <div style={{ display: 'flex', gap: 12, padding: '6px 0' }}>
          <div style={{ minWidth: 140, fontSize: 13, color: '#6B7280', fontWeight: 500 }}>Job URL</div>
          <div style={{ fontSize: 14 }}>
            <a
              href={referral.upwork_job_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--blue)', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              {referral.upwork_job_url} <ExternalLink size={12} />
            </a>
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, color: '#6B7280', fontWeight: 500, marginBottom: 6 }}>
            Job Description
          </div>
          <div style={{
            whiteSpace: 'pre-wrap', fontSize: 13, color: '#374151',
            background: '#F9FAFB', padding: 12, borderRadius: 6
          }}>
            {referral.upwork_job_description}
          </div>
        </div>
      </div>

      {referral.conversation && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginBottom: 12 }}>Conversation</h3>
          <ConversationView text={referral.conversation} />
        </div>
      )}

      {/* ================= CONVERT MODAL ================= */}
      {showConvert && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) closeConvert(); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 16
          }}
        >
          <div className="card" style={{ maxWidth: 720, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="card-header">
              <h3>Convert Referral to Lead</h3>
              <button
                onClick={closeConvert}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--gray-500)', display: 'inline-flex', padding: 4
                }}
              >
                <X size={20} />
              </button>
            </div>

            {convertError && (
              <div style={{
                padding: 12, marginBottom: 16,
                background: 'var(--light-red)', color: 'var(--red)',
                borderRadius: 6, fontSize: 14
              }}>
                {convertError}
              </div>
            )}

            <form onSubmit={handleConvertSubmit}>
              <div className="form-group">
                <label>Prospect Name *</label>
                <input
                  value={convertForm.prospect_name}
                  onChange={(e) => setConvertForm({ ...convertForm, prospect_name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>LinkedIn URL *</label>
                <input
                  value={convertForm.linkedin_url}
                  onChange={(e) => setConvertForm({ ...convertForm, linkedin_url: e.target.value })}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Company *</label>
                  <input
                    value={convertForm.company}
                    onChange={(e) => setConvertForm({ ...convertForm, company: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Industry</label>
                  <select
                    value={industryIsOther ? '__other__' : (convertForm.industry || '')}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '__other__') {
                        setIndustryIsOther(true);
                        setConvertForm({ ...convertForm, industry: '' });
                      } else {
                        setIndustryIsOther(false);
                        setConvertForm({ ...convertForm, industry: v });
                      }
                    }}
                  >
                    <option value="">Select industry...</option>
                    {INDUSTRY_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                    <option value="__other__">Other</option>
                  </select>
                  {industryIsOther && (
                    <input
                      type="text"
                      value={convertForm.industry}
                      onChange={(e) => setConvertForm({ ...convertForm, industry: e.target.value })}
                      placeholder="Enter industry..."
                      style={{ marginTop: 8 }}
                      autoFocus
                    />
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Classification *</label>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  {['hot', 'warm', 'cold'].map(c => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setConvertForm({ ...convertForm, classification: c })}
                      className={`badge ${CLASSIFICATION_BADGE[c]}`}
                      style={{
                        cursor: 'pointer', border: 'none',
                        padding: '8px 16px', fontSize: 14, fontWeight: 600,
                        opacity: convertForm.classification === c ? 1 : 0.5,
                        outline: convertForm.classification === c ? '2px solid var(--navy)' : 'none',
                        outlineOffset: 2
                      }}
                    >
                      {CLASSIFICATION_LABELS[c]}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Lead Date</label>
                  <input
                    type="date"
                    value={convertForm.lead_date}
                    onChange={(e) => setConvertForm({ ...convertForm, lead_date: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Assign to BDE *</label>
                  <select
                    value={convertForm.bde_id}
                    onChange={(e) => setConvertForm({ ...convertForm, bde_id: e.target.value })}
                    required
                  >
                    {assigneeOptions.length === 0 && (
                      <option value={user.id}>{user.name} (you)</option>
                    )}
                    {assigneeOptions.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.name}{u.id === user.id ? ' (you)' : ''} — {u.role}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Notes / Conversation</label>
                <textarea
                  value={convertForm.notes}
                  onChange={(e) => setConvertForm({ ...convertForm, notes: e.target.value })}
                  rows={12}
                  placeholder="Pre-filled with Upwork job context. Paste your LinkedIn first message at the bottom."
                />
                <p style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
                  After you send the first LinkedIn message, paste it at the bottom (using "BDE:" prefix) so the lead has the full conversation log.
                </p>
              </div>

              {/* Connection-note tracking — mandatory, same as the main Lead form. */}
              <div className="form-group">
                <label>Is note sent? *</label>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  {[{ val: true, label: 'Yes' }, { val: false, label: 'No' }].map((opt) => (
                    <button
                      key={String(opt.val)}
                      type="button"
                      onClick={() => setConvertForm({ ...convertForm, is_note_sent: opt.val })}
                      className="badge badge-gray"
                      style={{
                        cursor: 'pointer', border: 'none',
                        padding: '8px 16px', fontSize: 14, fontWeight: 600,
                        opacity: convertForm.is_note_sent === opt.val ? 1 : 0.5,
                        outline: convertForm.is_note_sent === opt.val ? '2px solid var(--navy)' : 'none',
                        outlineOffset: 2,
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 8 }}>
                  Was a LinkedIn connection note sent with the connection request?
                </p>
                {convertForm.is_note_sent === true && (
                  <div style={{ marginTop: 12 }}>
                    <label>Connection note message *</label>
                    <textarea
                      value={convertForm.connection_note}
                      onChange={(e) => setConvertForm({ ...convertForm, connection_note: e.target.value })}
                      rows={4}
                      placeholder="Paste the exact note message you sent with the connection request"
                    />
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                <button
                  type="button"
                  onClick={closeConvert}
                  disabled={submittingConvert}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingConvert}
                  className="btn btn-primary"
                >
                  {submittingConvert ? 'Converting...' : 'Convert to Lead'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ================= CLOSE MODAL ================= */}
      {showClose && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShowClose(false); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 16
          }}
        >
          <div className="card" style={{ maxWidth: 480, width: '100%' }}>
            <div className="card-header">
              <h3>Mark Referral Closed</h3>
              <button
                onClick={() => setShowClose(false)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--gray-500)', display: 'inline-flex', padding: 4
                }}
              >
                <X size={20} />
              </button>
            </div>
            <div className="form-group">
              <label>Reason *</label>
              <textarea
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                rows={4}
                maxLength={500}
                placeholder='e.g., "Already in our pipeline as lead #47", "Not a fit", "Profile inactive"'
                required
              />
              <p style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
                {closeReason.length}/500
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button
                onClick={() => setShowClose(false)}
                disabled={submittingClose}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCloseSubmit}
                disabled={submittingClose || !closeReason.trim()}
                className="btn btn-primary"
              >
                {submittingClose ? 'Closing...' : 'Close Referral'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpworkReferralDetail;
